import React, { useEffect, useState } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import dayjs from 'dayjs';
import { RealTimeTrendSettingModel, GetGraphDataListsType, GraphDataListsType } from './RealTimeTrendGraph';
import * as Constant from '../../../Constant';
import { ToValueMaxRound, ToValueMinRound } from '../../../function/CalculationModel';
import { GetUnitWithClc } from '../GroupSettings/TrendSetting/TrendSetting';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(
  createStyles({
    customTooltip: {
      background: '#FFFFEE',
      fontSize: '14px',
      padding: '10px 10px 0px 20px',
    },
  })
);

export type ChartInitType = {
  targetDay: string;
  selectGroupNo: number;
  trendSettingList: RealTimeTrendSettingModel[];
};

type GraphData = {
  lineName: string;
  lineNo: number;
  points: GraphPoint[];
  valueUpper: number;
  valueUpperLimit: number;
  selectFlg: boolean;
  infoColor: string;
  yMax: number;
  yMin: number;
};
type GraphPoint = {
  datetime: Date;
  value: number;
};

type GraphSetting = {
  upper: number;
  upperLimit: number;
  yMin: number;
  yMax: number;
  backColor: string;
  memoryFontColor: string;
  gridColor: string;
  UnitSymbol: string;
  xMin: string; //時間軸最小文字列
  xMax: string; //時間軸最大文字列
};

const CreateGData = (rows: GraphDataListsType): GraphData[] => {
  var ret: GraphData[] = [];
  for (var i in rows.GraphDataList) {
    var item: GraphData = {
      lineNo: Number(i),
      valueUpper: rows.GraphDataList[i].UpperValue,
      valueUpperLimit: rows.GraphDataList[i].UpperLimitValue,
      selectFlg: true,
      points: [],
      infoColor: '',
      lineName: '',
      yMax: 0,
      yMin: 0,
    };

    for (var j in rows.GraphDataList[i].ValueDateTime) {
      item.points.push({ datetime: rows.GraphDataList[i].ValueDateTime[j], value: rows.GraphDataList[i].Value[j] });
    }
    var max = Math.round(Math.max(...item.points.map((p) => p.value)));
    var min = Math.min(...item.points.map((p) => p.value));

    var maxA: number;
    var minA: number;
    maxA = ToValueMaxRound(max);
    minA = ToValueMinRound(min);
    item.yMax = maxA;
    item.yMin = minA;

    ret.push(item);
  }
  return ret;
};

type Props = {
  selectGroupNo: number;
  trendSettingList: RealTimeTrendSettingModel[];

  chartContainerStyle: string;
  chartInitData: ChartInitType;
  onYMinMaxChange: (yMin: number, yMax: number) => void;
  yMin: number;
  yMax: number;
};

const RealTimeTrendGraphChart: React.FC<Props> = (props) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  console.time('[RealTimeTrendGraphChart] - timer');

  const [graphData, setGraphData] = useState<GraphData[]>([]);

  const [graphSetting, setGraphSetting] = useState<GraphSetting>({
    upper: 0,
    upperLimit: 0,
    yMax: 0,
    yMin: 0,
    backColor: Constant.GraphThemeLight.BACK_COLOR,
    memoryFontColor: Constant.GraphThemeLight.MEMORY_FONT_COLOR,
    gridColor: Constant.GraphThemeLight.GRID_COLOR,
    UnitSymbol: '',
    xMax: '',
    xMin: '',
  });

  const setData = (graphDataList: GraphData[], trendSettingList: RealTimeTrendSettingModel[]) => {
    if (trendSettingList != null) {
      var target = trendSettingList.find((item) => item.IsRowSelect);
      console.debug(['★target', target]);

      if (target != null) {
        graphSetting.upper = Number(target.RealTimeTrendValueUpper);
        graphSetting.upperLimit = Number(target.RealTimeTrendValueUpperLimit);

        //Y軸取得;
        const yMin = graphDataList == null ? 0 : Math.min(...graphDataList.map((p) => p.yMin));
        const yMax = graphDataList == null ? 0 : Math.max(...graphDataList.map((p) => p.yMax));

        graphSetting.yMax = yMax;
        graphSetting.yMin = yMin;
        graphSetting.UnitSymbol = GetUnitWithClc(
          Number(target.PLCNo),
          target.UnitSymbol,
          target.Clc,
          Number(target.MeasureKindNo),
          target.Kind
        );
        //console.debug([target, '★yMax', graphSetting.yMax]);
        //console.debug([target, '★yMin', graphSetting.yMin]);

        //X軸最大値セット
        if (graphDataList.length !== 0) {
          let pointCount = graphDataList[0].points.length;
          if (pointCount !== 0) {
            graphSetting.xMax = graphDataList[0].points[pointCount - 1].datetime.toString();
            graphSetting.xMin = graphDataList[0].points[0].datetime.toString();
          }
        }

        console.debug(['trendSettingList,graphSetting', target, graphSetting]);
        props.onYMinMaxChange(graphSetting.yMin, graphSetting.yMax);
        setGraphSetting({ ...graphSetting });
      }
      for (var i in graphDataList) {
        if (props.trendSettingList[i].PLCNo != '0') {
          graphDataList[i].selectFlg = props.trendSettingList[i].IsShow;
          graphDataList[i].infoColor = props.trendSettingList[i].LineColor;
        }
      }
    }
  };

  useEffect(() => {
    var gs = graphSetting;
    gs.yMin = props.yMin;
    gs.yMax = props.yMax;
    console.debug(['useEffect props.yMax, props.yMin', props.yMax, props.yMin]);
    setGraphSetting({ ...gs });
  }, [props.yMax, props.yMin]);

  useEffect(() => {
    var dt = props.chartInitData;
    GetGraphDataListsType(dt.selectGroupNo.toString(), dt.targetDay).then((result) => {
      var tList = CreateGData(result);
      setData(tList, dt.trendSettingList);
      setGraphData(tList);
      console.debug(['props.chartInitData', dt]);
      console.debug(['tList', tList]);
      console.timeEnd('[RealTimeTrendGraphChart] GetGraphDataListsType - timer');
    });
  }, [props.chartInitData]);

  useEffect(() => {
    console.time('[RealTimeTrendGraphChart] trendSettingListCnage - timer');
    setData(graphData, props.trendSettingList);
    setGraphData([...graphData]);
    console.debug('★props.trendSettingList- useEffect★');
    console.debug(['★props.trendSettingList', props.trendSettingList]);
    console.debug(['★graphData', graphData]);
    console.timeEnd('[RealTimeTrendGraphChart] trendSettingListCnage - timer');
  }, [props.trendSettingList]);

  console.timeEnd('[RealTimeTrendGraphChart] - timer');

  const CustomTooltip = ({ active, payload, label }: any) => {
    //console.debug(['payload', payload]);
    if (active) {
      return (
        <div className={classes.customTooltip}>
          <div className="label">{dayjs(label).format('YY/MM/DD HH:mm')}</div>
          {payload?.map((r: any, index: any) => {
            return (
              <div style={{ color: r.color }}>
                {r.name} : {Number(r.value).toFixed(3)}
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  /**
   * x軸時間表示作成
   * @param min
   * @param max
   * @param dispSpan //表示周期
   * @returns
   */
  const GetMemoryScaleByTimeString = (min: string, max: string, dispSpan: number): string[] => {
    let ans: string[] = [];
    ans.push(min);
    let bairitu = 4 * dispSpan;

    for (let i = 0; i < 5; i++) {
      let addTime = dayjs(min)
        .add(bairitu + bairitu * i, 'h')
        .format('YYYY-MM-DDTHH:mm:ss')
        .toString();
      ans.push(addTime);
    }
    ans.push(max);
    return ans;
  };

  //allowDuplicatedCategory 重複したメモリを重ねる
  return (
    <ResponsiveContainer className={props.chartContainerStyle} width="99%">
      <LineChart
        style={{ background: graphSetting.backColor }}
        width={400}
        height={300}
        margin={{
          top: 10,
          right: 30,
          left: 1,
          bottom: 0,
        }}
      >
        <CartesianGrid stroke={graphSetting.gridColor} strokeDasharray="3 3" />
        <XAxis
          dataKey="datetime"
          type="category"
          allowDuplicatedCategory={false}
          stroke={graphSetting.memoryFontColor}
          ticks={GetMemoryScaleByTimeString(
            graphSetting.xMin,
            graphSetting.xMax,
            props.trendSettingList.length !== 0 ? props.trendSettingList[0].DispSpan : 1
          )}
          //interval={240}
          tickFormatter={(props) => dayjs(props).format(t('RealTimeTrendGraphChart.DD日HH時mm分', 'DD日HH時mm分'))}
          height={30}
          tick={{ dy: 10 }}
          padding={{ right: 0, left: 0 }}
        />
        <YAxis
          padding={{ top: 0, bottom: 0 }}
          allowDataOverflow={true}
          dataKey="value"
          type="number"
          stroke={graphSetting.memoryFontColor}
          domain={[graphSetting.yMin, graphSetting.yMax]}
          tickFormatter={(props) => props.toFixed(2)}
          width={80}
        >
          <Label angle={270} position="insideLeft" style={{ textAnchor: 'middle', padding: 0 }}>
            {graphSetting.UnitSymbol}
          </Label>
        </YAxis>
        <Tooltip content={<CustomTooltip />} />
        <ReferenceLine y={graphSetting.upper} stroke={Constant.LIGHT_FAULT_LINE_COLOR} alwaysShow={false} />
        <ReferenceLine y={graphSetting.upperLimit} stroke={Constant.SERIOUS_FAULT_LINE_COLOR} alwaysShow={false} />
        {graphData
          ?.filter((r) => r.selectFlg === true)
          .map((row, index) => (
            <Line
              key={index}
              isAnimationActive={false}
              dataKey="value"
              stroke={row.infoColor}
              data={row.points}
              strokeWidth={2}
              dot={false}
              name={row.lineNo.toString()}
              hide={!row.selectFlg}
            />
          ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
export default RealTimeTrendGraphChart;
